import { ref } from 'vue';
import { opsApi } from '@/api/ops';
import { useOpsToast } from '@/composables/ops/useOpsToast';

/**
 * 运维管理 Composable
 * 管理运维相关的状态和逻辑
 */
export function useOpsManagement() {
  // 使用运维平台专用的 Toast（独立于用户界面）
  const { addToast } = useOpsToast();

  // ========== 管理功能状态 ==========
  const users = ref([]);
  const userSearchKeyword = ref('');
  const userSearchRole = ref('');
  const usersLoading = ref(false);
  // 用户管理分页
  const usersPage = ref(1);
  const usersPageSize = ref(10);
  const usersTotal = ref(0);

  const files = ref([]);
  const fileFilterResolved = ref(null);
  const filesLoading = ref(false);
  // 文件管理分页
  const filesPage = ref(1);
  const filesPageSize = ref(10);
  const filesTotal = ref(0);

  const feedbacks = ref([]);
  const feedbacksLoading = ref(false);
  // 反馈管理分页
  const feedbacksPage = ref(1);
  const feedbacksPageSize = ref(10);
  const feedbacksTotal = ref(0);

  const newOp = ref({
    username: '',
    password: '',
    name: '',
    phone: '',
    email: ''
  });

  const operationLogs = ref([]);
  // 操作日志分页
  const logsPage = ref(1);
  const logsPageSize = ref(10);
  const logsTotal = ref(0);

  // ========== 监控功能状态 ==========
  const onlineUsers = ref([]);
  const accessStats = ref({});
  const mysqlMonitor = ref(null);
  const hosts = ref([]);
  const diskMonitor = ref(null);
  const memoryMonitor = ref(null);
  const cpuLoad = ref(null);
  const monitorLoading = ref(false);

  // 监控历史数据
  const monitorHistory = ref({});

  // 报警阈值
  const alarmThresholds = ref({});

  // ========== 管理功能方法 ==========

  /**
   * 加载用户列表
   */
  const loadUsers = async (page = null, pageSize = null) => {
    usersLoading.value = true;
    try {
      const currentPage = page !== null ? page : usersPage.value;
      const currentPageSize = pageSize !== null ? pageSize : usersPageSize.value;
      
      const res = await opsApi.getAllUsers({
        keyword: userSearchKeyword.value,
        role: userSearchRole.value,
        page: currentPage,
        pageSize: currentPageSize
      });
      if (res?.data) {
        // 如果返回的是分页数据
        if (res.data.list) {
          users.value = res.data.list;
          usersTotal.value = res.data.total || 0;
          usersPage.value = res.data.page || currentPage;
          usersPageSize.value = res.data.pageSize || currentPageSize;
        } else {
          // 兼容旧格式（非分页数据）
          users.value = Array.isArray(res.data) ? res.data : [];
          usersTotal.value = users.value.length;
        }
      }
    } catch (error) {
      console.error('加载用户列表失败:', error);
      addToast('加载用户列表失败', 'error', 3000);
    } finally {
      usersLoading.value = false;
    }
  };

  /**
   * 切换用户状态
   */
  const toggleUserStatus = async (user) => {
    try {
      const newStatus = user.status === 1 ? 0 : 1;
      const res = await opsApi.toggleUserStatus(user.id, newStatus);
      if (res?.code === 200) {
        await loadUsers();
        addToast(`用户${newStatus === 1 ? '已启用' : '已禁用'}`, 'success', 2000);
      } else {
        addToast(res?.message || '操作失败', 'error', 3000);
      }
    } catch (error) {
      console.error('切换用户状态失败:', error);
      addToast('切换用户状态失败', 'error', 3000);
    }
  };

  /**
   * 更新用户信息
   */
  const updateUser = async (userId, userData) => {
    try {
      const res = await opsApi.updateUser(userId, userData);
      if (res?.code === 200) {
        await loadUsers();
        addToast('用户信息更新成功', 'success', 2000);
        return true;
      } else {
        addToast(res?.message || '更新失败', 'error', 3000);
        return false;
      }
    } catch (error) {
      console.error('更新用户信息失败:', error);
      const errorMsg = error?.response?.data?.message || error?.message || '更新用户信息失败';
      addToast(errorMsg, 'error', 3000);
      return false;
    }
  };

  /**
   * 删除用户（单个）
   */
  const deleteUser = async (userId) => {
    try {
      const res = await opsApi.deleteUser(userId);
      if (res?.code === 200) {
        await loadUsers();
        addToast('用户删除成功', 'success', 2000);
        return true;
      } else {
        addToast(res?.message || '删除失败', 'error', 3000);
        return false;
      }
    } catch (error) {
      console.error('删除用户失败:', error);
      const errorMsg = error?.response?.data?.message || error?.message || '删除用户失败';
      addToast(errorMsg, 'error', 3000);
      return false;
    }
  };

  /**
   * 批量删除用户
   */
  const batchDeleteUsers = async (userIds) => {
    try {
      const res = await opsApi.batchDeleteUsers(userIds);
      if (res?.code === 200) {
        await loadUsers();
        addToast(`成功删除 ${res?.data?.deleted || userIds.length} 个用户`, 'success', 2000);
        return true;
      } else {
        addToast(res?.message || '批量删除失败', 'error', 3000);
        return false;
      }
    } catch (error) {
      console.error('批量删除用户失败:', error);
      const errorMsg = error?.response?.data?.message || error?.message || '批量删除用户失败';
      addToast(errorMsg, 'error', 3000);
      return false;
    }
  };

  /**
   * 加载文件列表
   */
  const loadFiles = async (page = null, pageSize = null) => {
    filesLoading.value = true;
    try {
      const currentPage = page !== null ? page : filesPage.value;
      const currentPageSize = pageSize !== null ? pageSize : filesPageSize.value;
      
      const res = await opsApi.getUserFiles({
        resolved: fileFilterResolved.value,
        page: currentPage,
        pageSize: currentPageSize
      });
      if (res?.data) {
        // 如果返回的是分页数据
        if (res.data.list) {
          files.value = res.data.list;
          filesTotal.value = res.data.total || 0;
          filesPage.value = res.data.page || currentPage;
          filesPageSize.value = res.data.pageSize || currentPageSize;
        } else {
          // 兼容旧格式（非分页数据）
          files.value = Array.isArray(res.data) ? res.data : [];
          filesTotal.value = files.value.length;
        }
      }
    } catch (error) {
      console.error('加载文件列表失败:', error);
      addToast('加载文件列表失败', 'error', 3000);
    } finally {
      filesLoading.value = false;
    }
  };

  /**
   * 标记文件为已解决
   */
  const markFileResolved = async (fileId) => {
    try {
      const res = await opsApi.markFileResolved(fileId);
      if (res?.code === 200) {
        await loadFiles();
        addToast('文件已标记为已解决', 'success', 2000);
      } else {
        addToast(res?.message || '操作失败', 'error', 3000);
      }
    } catch (error) {
      console.error('标记文件失败:', error);
      addToast('标记文件失败', 'error', 3000);
    }
  };

  /**
   * 切换文件已解决/未解决
   */
  const toggleFileResolved = async (fileId) => {
    try {
      const res = await opsApi.toggleFileResolved(fileId);
      if (res?.code === 200) {
        await loadFiles();
        addToast('状态已更新', 'success', 2000);
      } else {
        addToast(res?.message || '操作失败', 'error', 3000);
      }
    } catch (error) {
      console.error('切换文件状态失败:', error);
      addToast('切换文件状态失败', 'error', 3000);
    }
  };

  /**
   * 加载反馈列表
   */
  const loadFeedbacks = async (page = null, pageSize = null) => {
    feedbacksLoading.value = true;
    try {
      const currentPage = page !== null ? page : feedbacksPage.value;
      const currentPageSize = pageSize !== null ? pageSize : feedbacksPageSize.value;
      const res = await opsApi.getFeedbackList({
        page: currentPage,
        pageSize: currentPageSize
      });
      if (res?.data) {
        // 处理返回的数据，添加用户名
        if (Array.isArray(res.data)) {
          feedbacks.value = res.data.map(item => ({
            ...item,
            userName: item.userName || item.user_name || '匿名用户'
          }));
          feedbacksTotal.value = res.data.length;
        } else if (res.data.list) {
          feedbacks.value = res.data.list.map(item => ({
            ...item,
            userName: item.userName || item.user_name || '匿名用户'
          }));
          feedbacksTotal.value = res.data.total || 0;
          feedbacksPage.value = res.data.page || currentPage;
          feedbacksPageSize.value = res.data.pageSize || currentPageSize;
        }
      }
    } catch (error) {
      console.error('加载反馈列表失败:', error);
      addToast('加载反馈列表失败', 'error', 3000);
    } finally {
      feedbacksLoading.value = false;
    }
  };

  /**
   * 更新反馈状态
   */
  const updateFeedbackStatus = async (feedbackId, status, adminRemark = null) => {
    try {
      const res = await opsApi.updateFeedbackStatus(feedbackId, status, adminRemark);
      if (res?.code === 200) {
        addToast('反馈状态已更新', 'success', 2000);
        return { ok: true };
      } else {
        addToast(res?.message || '操作失败', 'error', 3000);
        return { ok: false, message: res?.message || '操作失败' };
      }
    } catch (error) {
      console.error('更新反馈状态失败:', error);
      addToast('更新反馈状态失败', 'error', 3000);
      return { ok: false, message: '更新反馈状态失败' };
    }
  };

  /**
   * 删除反馈
   */
  const deleteFeedback = async (feedbackId) => {
    try {
      const res = await opsApi.deleteFeedback(feedbackId);
      if (res?.code === 200) {
        addToast('反馈已删除', 'success', 2000);
        return { ok: true };
      } else {
        addToast(res?.message || '删除失败', 'error', 3000);
        return { ok: false, message: res?.message || '删除失败' };
      }
    } catch (error) {
      console.error('删除反馈失败:', error);
      addToast('删除反馈失败', 'error', 3000);
      return { ok: false, message: '删除反馈失败' };
    }
  };

  /**
   * 创建运维管理员
   */
  const createOp = async () => {
    const payload = {
      username: (newOp.value.username || '').trim(),
      password: (newOp.value.password || '').trim(),
      name: (newOp.value.name || '').trim(),
      phone: (newOp.value.phone || '').trim(),
      email: (newOp.value.email || '').trim()
    };

    if (!payload.username || !payload.password || !payload.name || !payload.phone || !payload.email) {
      addToast('用户名、密码、姓名、电话、邮箱均不能为空', 'error', 3000);
      return { ok: false, message: '用户名、密码、姓名、电话、邮箱均不能为空' };
    }
    try {
      const res = await opsApi.createOp(payload);
      if (res?.code === 200) {
        newOp.value = {
          username: '',
          password: '',
          name: '',
          phone: '',
          email: ''
        };
        await loadUsers();
        addToast('运维管理员创建成功', 'success', 2000);
      return { ok: true };
    } else {
        const msg = res?.message || res?.msg || '创建失败';
        addToast(msg, 'error', 3000);
        return { ok: false, message: msg };
      }
    } catch (error) {
      console.error('创建运维管理员失败:', error);
      const msg = error?.response?.data?.message
        || error?.response?.data?.msg
        || error?.message
        || '创建运维管理员失败';
      addToast(msg, 'error', 3000);
      return { ok: false, message: msg };
    }
  };

  /**
   * 加载操作日志
   */
  const loadOperationLogs = async (userId, action, limit, page = null, pageSize = null) => {
    try {
      const currentPage = page !== null ? page : logsPage.value;
      const currentPageSize = pageSize !== null ? pageSize : logsPageSize.value;
      
      // 构建请求参数，只传递有效的值
      const params = {
        page: currentPage,
        pageSize: currentPageSize
      };
      
      // 只有当 userId 不为 null 且有效时才添加
      if (userId != null && userId > 0) {
        params.userId = userId;
      }
      
      // 只有当 action 不为 null 且不为空字符串时才添加
      if (action != null) {
        const actionStr = typeof action === 'string' ? action.trim() : String(action);
        if (actionStr !== '') {
          params.action = actionStr;
        }
      }
      
      // 只有当 limit 不为 null 时才添加
      if (limit != null && limit > 0) {
        params.limit = limit;
      }
      
      const res = await opsApi.getUserLogs(params);
      if (res?.data) {
        // 如果返回的是分页数据
        if (res.data.list) {
          operationLogs.value = res.data.list;
          logsTotal.value = res.data.total || 0;
          logsPage.value = res.data.page || currentPage;
          logsPageSize.value = res.data.pageSize || currentPageSize;
        } else {
          // 兼容旧格式（非分页数据）
          operationLogs.value = Array.isArray(res.data) ? res.data : [];
          logsTotal.value = operationLogs.value.length;
          // 保持前端分页状态可响应
          logsPage.value = currentPage;
          logsPageSize.value = currentPageSize;
        }
      }
    } catch (error) {
      console.error('加载操作日志失败:', error);
    }
  };

  // ========== 监控功能方法 ==========

  // 规范化 MySQL 监控数据，兼容不同字段名，避免前端全 0
  const normalizeMysqlMonitor = (raw = {}) => {
    const safeNum = (v, fb = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fb;
    };
    const pick = (...keys) => {
      for (const k of keys) {
        if (raw[k] !== undefined && raw[k] !== null) return raw[k];
      }
      return undefined;
    };

    // 连接
    const threadsConnected = safeNum(
      pick('threadsConnected', 'threads_connected', 'connectionCount', 'connections', 'totalConnections', 'Threads_connected')
    );
    const maxConn = safeNum(
      pick('maxConnections', 'max_connections', 'max_conn', 'max_conn_limit', 'maxConn', 'Max_connections'),
      0
    );
    const threadsRunning = safeNum(
      pick('threadsRunning', 'threads_running', 'activeConnections', 'connRunning', 'Threads_running')
    );
    const connUsage = maxConn > 0
      ? (threadsConnected / maxConn) * 100
      : safeNum(pick('connUsage', 'connectionUsage', 'conn_usage_pct'));

    // QPS / TPS
    const qps = safeNum(pick('qps', 'QPS', 'queriesPerSecond', 'queries_per_second', 'queries'));
    const tpsRaw = pick(
      'tps',
      'TPS',
      'transactionsPerSecond',
      'transactions_per_second'
    );
    const tps = safeNum(tpsRaw, (safeNum(raw.comCommit) + safeNum(raw.comRollback)) / Math.max(1, safeNum(raw.seconds, 1)));

    // 慢查询
    const slowQueries = safeNum(pick('slowQueries', 'slowQueryCount', 'slow', 'Slow_queries'));
    const totalQueries = safeNum(pick('totalQueries', 'questions', 'Queries', 'queries'));
    const slowRate = totalQueries > 0 ? (slowQueries / totalQueries) * 100 : safeNum(pick('slowRate', 'slow_rate_pct'));

    // 磁盘占用（数据目录）
    const dataDirUsed = safeNum(pick('dataDirUsed', 'diskUsed', 'dataSize', 'data_dir_used_bytes'));
    const dataDirSize = safeNum(pick('dataDirSize', 'diskTotal', 'dataTotal', 'data_dir_total_bytes'));
    const diskUsagePct = dataDirSize > 0
      ? (dataDirUsed / dataDirSize) * 100
      : safeNum(pick('diskUsagePct', 'dataDirUsage', 'disk_usage_pct'));

    // 复制状态
    const replicationDelay = safeNum(pick('replicationDelay', 'replDelay', 'lag', 'secondsBehindMaster'));
    let replicationStatus = pick('replicationStatus', 'replStatus');
    if (!replicationStatus && raw.slaveIORunning && raw.slaveSQLRunning) {
      replicationStatus = (String(raw.slaveIORunning).toUpperCase() === 'YES' &&
        String(raw.slaveSQLRunning).toUpperCase() === 'YES') ? 'OK' : 'ERR';
    }
    if (!replicationStatus) replicationStatus = replicationDelay === 0 ? 'OK' : 'DEGRADED';

    // 缓冲池 & IO
    const bufferHitRatio = safeNum(pick('bufferHitRatio', 'innodbHitRate', 'bufferPoolHitRate', 'buffer_hit_pct'));
    const dirtyPageRatio = safeNum(pick('dirtyPageRatio', 'dirtyPagesPct', 'dirtyPages', 'dirty_pct'));
    const innodbReadIops = safeNum(pick('readIops', 'innodbReadIops', 'read_iops'));
    const innodbWriteIops = safeNum(pick('writeIops', 'innodbWriteIops', 'write_iops'));

    // 备份 / binlog / 日志
    const backupStatus = pick('backupStatus', 'backup_state') ?? '';
    const binlogUsagePct = safeNum(pick('binlogUsagePct', 'binlogUsedPct', 'binlog_usage_pct'));
    const backupSize = safeNum(pick('backupSize', 'lastBackupSize'));
    const lastBackupAt = pick('lastBackupAt', 'lastBackupTime');

    // 其他
    const uptime = pick('uptime', 'uptimeHuman') ?? '';
    const status = pick('status', 'alive') ?? 'unknown';
    const connRejects = safeNum(pick('connRejects', 'connectionRejects', 'rejects'));
    const aborted = safeNum(pick('aborted', 'abortedConnects', 'aborted_connects'));
    const restartCount = safeNum(pick('restartCount', 'restarts24h', 'restarts'), 0);

    return {
      ...raw,
      threadsConnected,
      threadsRunning,
      totalConnections: threadsConnected,
      maxConnections: maxConn,
      connUsage,
      qps,
      tps,
      slowQueries,
      slowQueryCount: slowQueries,
      slowRate,
      dataDirUsed,
      dataDirSize,
      diskUsagePct,
      replicationDelay,
      replicationStatus,
      bufferHitRatio,
      innodbHitRate: bufferHitRatio,
      dirtyPageRatio,
      dirtyPagesPct: dirtyPageRatio,
      readIops: innodbReadIops,
      writeIops: innodbWriteIops,
      backupStatus,
      binlogUsagePct,
      backupSize,
      lastBackupAt,
      uptime,
      status,
      connRejects,
      aborted,
      restartCount
    };
  };

  /**
   * 加载监控数据
   */
  const loadMonitorData = async () => {
    monitorLoading.value = true;
    try {
      // 加载在线用户
      const onlineRes = await opsApi.getOnlineUsers();
      if (onlineRes?.data) {
        onlineUsers.value = onlineRes.data;
      }

      // 加载访问统计
      const statsRes = await opsApi.getAccessStats();
      if (statsRes?.data) {
        accessStats.value = statsRes.data;
      }

      // 加载MySQL监控
      const mysqlRes = await opsApi.getMysqlMonitor();
      if (mysqlRes?.data) {
        mysqlMonitor.value = normalizeMysqlMonitor(mysqlRes.data);
      }

      // 加载主机列表
      const hostsRes = await opsApi.getHosts();
      if (hostsRes?.data) {
        hosts.value = hostsRes.data;
      }

      // 加载磁盘监控
      const diskRes = await opsApi.getDiskMonitor();
      if (diskRes?.data) {
        diskMonitor.value = diskRes.data;
      }

      // 加载内存监控
      const memoryRes = await opsApi.getMemoryMonitor();
      if (memoryRes?.data) {
        memoryMonitor.value = memoryRes.data;
      }

      // 加载CPU负载
      const cpuRes = await opsApi.getCpuLoad();
      if (cpuRes?.data) {
        cpuLoad.value = cpuRes.data;
      }

    } catch (error) {
      console.error('加载监控数据失败:', error);
    } finally {
      monitorLoading.value = false;
    }
  };

  /**
   * 加载监控历史数据
   */
  const loadMonitorHistory = async (metricType, hostId, hours = 24) => {
    try {
      const res = await opsApi.getMonitorHistory(metricType, hostId, hours);
      if (res?.data) {
        monitorHistory.value[metricType] = res.data;
      }
    } catch (error) {
      console.error('加载监控历史数据失败:', error);
    }
  };

  /**
   * 更新报警阈值
   */
  const updateThreshold = async (metricType, hostId, threshold, unit = '%') => {
    try {
      const res = await opsApi.updateAlarmThreshold(metricType, hostId, threshold, unit);
      if (res?.code === 200) {
        addToast('报警阈值更新成功', 'success', 2000);
        // 重新加载监控数据
        await loadMonitorData();
      } else {
        addToast(res?.message || '更新失败', 'error', 3000);
      }
    } catch (error) {
      console.error('更新阈值失败:', error);
      addToast('更新阈值失败', 'error', 3000);
    }
  };

  // ========== 工具函数 ==========

  /**
   * 格式化文件大小
   */
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    if (typeof bytes !== 'number') {
      bytes = parseFloat(bytes) || 0;
    }
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  /**
   * 获取角色样式类
   */
  const getRoleClass = (role) => {
    const classes = {
      owner: 'bg-purple-500/20 text-purple-400',
      admin: 'bg-blue-500/20 text-blue-400',
      observer: 'bg-green-500/20 text-green-400',
      restricted: 'bg-yellow-500/20 text-yellow-400',
      op: 'bg-red-500/20 text-red-400'
    };
    return classes[role] || 'bg-slate-500/20 text-slate-400';
  };

  return {
    // 管理功能状态
    users,
    userSearchKeyword,
    userSearchRole,
    usersLoading,
    usersPage,
    usersPageSize,
    usersTotal,
    files,
    fileFilterResolved,
    filesLoading,
    filesPage,
    filesPageSize,
    filesTotal,
    feedbacks,
    feedbacksLoading,
    feedbacksPage,
    feedbacksPageSize,
    feedbacksTotal,
    newOp,
    operationLogs,
    logsPage,
    logsPageSize,
    logsTotal,
    // 监控功能状态
    onlineUsers,
    accessStats,
    mysqlMonitor,
    hosts,
    diskMonitor,
    memoryMonitor,
    cpuLoad,
    monitorLoading,
    monitorHistory,
    alarmThresholds,
    // 管理功能方法
    loadUsers,
    toggleUserStatus,
    updateUser,
    deleteUser,
    batchDeleteUsers,
    loadFiles,
    markFileResolved,
    toggleFileResolved,
    loadFeedbacks,
    updateFeedbackStatus,
    deleteFeedback,
    createOp,
    loadOperationLogs,
    // 监控功能方法
    loadMonitorData,
    loadMonitorHistory,
    updateThreshold,
    // 工具函数
    formatFileSize,
    getRoleClass
  };
}
